import { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { useAuth } from '@/lib/platform/AuthProvider';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  limit as firestoreLimit,
} from 'firebase/firestore';
import { useChatUsers, useCachedChatUsers } from '@/lib/api/useChat';
import {
  getFirestoreDb,
} from '@/lib/firebase';
import { ChatAPI } from '@shared/types/api';
import type { ConversationData, ChatUser } from './types';

interface ConversationsContextValue {
  conversations: ConversationData[];
  userCache: Record<string, ChatUser>;
  userId: string | null;
  totalUnreadCount: number;
  isLoading: boolean;
  error: string | null;
}

const ConversationsContext = createContext<ConversationsContextValue | null>(null);

export function useConversations() {
  const context = useContext(ConversationsContext);
  if (!context) {
    throw new Error('useConversations must be used within a ConversationsProvider');
  }
  return context;
}

// Safe hook that returns null if outside provider (for Navigation)
export function useConversationsSafe(): ConversationsContextValue | null {
  return useContext(ConversationsContext);
}

interface ConversationsProviderProps {
  children: ReactNode;
}

export function ConversationsProvider({ children }: ConversationsProviderProps) {
  const { userId } = useAuth();

  // Conversations state
  const [conversations, setConversations] = useState<ConversationData[]>([]);

  // User cache
  const [userCache, setUserCache] = useState<Record<string, ChatAPI.ChatUser>>({});
  const fetchedUserIdsRef = useRef<Set<string>>(new Set()); // Track which users we've already fetched
  const fetchChatUsers = useChatUsers();
  const { data: cachedUsers } = useCachedChatUsers();

  // Subscribe to conversations
  useEffect(() => {
    if (!userId) return;

    const db = getFirestoreDb();
    const conversationsRef = collection(db, 'conversations');
    const q = query(
      conversationsRef,
      where('participants', 'array-contains', userId),
      orderBy('updatedAt', 'desc'),
      firestoreLimit(20)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const convos: ConversationData[] = [];
        snapshot.forEach((doc) => {
          convos.push({ id: doc.id, ...doc.data() } as ConversationData);
        });
        setConversations(convos);

        // Fetch user info for any new participants we haven't fetched yet
        const allParticipantIds = new Set<string>();
        convos.forEach((c) => c.participants.forEach((p) => allParticipantIds.add(p)));
        
        // Use ref to track fetched IDs (avoids stale closure issue)
        const newUserIds = Array.from(allParticipantIds).filter(
          (id) => !fetchedUserIdsRef.current.has(id) && id !== userId
        );
        
        if (newUserIds.length > 0) {
          // Mark as fetched immediately to prevent duplicate requests
          newUserIds.forEach((id) => fetchedUserIdsRef.current.add(id));
          fetchChatUsers.mutate(newUserIds);
        }
      },
      (error) => {
        console.error('Error fetching conversations:', error);
      }
    );

    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // Update user cache when fetched
  useEffect(() => {
    if (cachedUsers) {
      setUserCache((prev) => ({ ...prev, ...cachedUsers }));
    }
  }, [cachedUsers]);

  // Calculate total unread count
  const totalUnreadCount = conversations.reduce((total, convo) => {
    return total + (convo.unreadCounts?.[userId || ''] || 0);
  }, 0);

  // const error = !firebaseAvailable || firebaseError || authError ? (firebaseError || 'Chat unavailable') : null;

  const value: ConversationsContextValue = {
    conversations,
    userCache,
    userId,
    totalUnreadCount,
    isLoading: false,
    error: null,
  };

  return (
    <ConversationsContext.Provider value={value}>
      {children}
    </ConversationsContext.Provider>
  );
}
